import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { profileApi, profileKeys } from "@/features/profile/api";
import type { ProfileFileKind } from "@/features/profile/types";

export function useMyProfile() {
  return useQuery({
    queryKey: profileKeys.mine(),
    queryFn: profileApi.get,
  });
}

/** The departments to choose from, as HR keeps them in Settings. */
export function useDepartments() {
  return useQuery({
    queryKey: profileKeys.departments(),
    queryFn: profileApi.departments,
    select: (data) => data.departments,
    staleTime: 5 * 60_000,
  });
}

export function useMyPay() {
  return useQuery({ queryKey: profileKeys.pay(), queryFn: profileApi.pay });
}

export function useCompleteness() {
  return useQuery({
    queryKey: profileKeys.completeness(),
    queryFn: profileApi.completeness,
  });
}

/** Every profile write refreshes the profile and its completeness together. */
function useProfileMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useUpdateProfile() {
  return useProfileMutation((changes: Record<string, unknown>) => profileApi.update(changes));
}

export function useUploadProfileFile() {
  return useProfileMutation(({ kind, file }: { kind: ProfileFileKind; file: File }) =>
    profileApi.uploadFile(kind, file),
  );
}

export function useRemoveProfileFile() {
  return useProfileMutation((kind: ProfileFileKind) => profileApi.removeFile(kind));
}

/** Saves a drawn signature. The server refuses it once the account is approved. */
export function useSaveSignature() {
  return useProfileMutation((dataUrl: string) => profileApi.saveSignature(dataUrl));
}

export function useAcceptDeclaration() {
  return useProfileMutation((version: string) => profileApi.acceptDeclaration(version));
}

export function useSaveCorrections() {
  return useProfileMutation((changes: Record<string, unknown>) =>
    profileApi.saveCorrections(changes),
  );
}

export function useSendCorrectionsBack() {
  return useProfileMutation(() => profileApi.sendCorrectionsBack());
}

export function useSubmitProfile() {
  return useProfileMutation(() => profileApi.submit());
}

export function useMyChangeRequests() {
  return useQuery({
    queryKey: profileKeys.changeRequests(),
    queryFn: profileApi.listChangeRequests,
  });
}

/**
 * Uploads a file to go with a change request.
 *
 * Not a profile mutation: the record does not change until HR applies the
 * request, so there is nothing to refresh.
 */
export function useUploadForChangeRequest() {
  return useMutation({
    mutationFn: ({ kind, file }: { kind: ProfileFileKind; file: File }) =>
      profileApi.uploadForChangeRequest(kind, file),
  });
}

export function useRequestChange() {
  return useProfileMutation((changes: Record<string, unknown>) =>
    profileApi.requestChange(changes),
  );
}
